import * as React from "react"

import { cn } from "@/utils"

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
          "ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export interface InputNumberProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
  /** Called with the numeric value */
  onChange?: (value: number | null) => void
  /** Prefix text/element */
  prefix?: React.ReactNode
  /** Suffix text/element (addon-after equivalent) */
  addonAfter?: React.ReactNode
  /** Addon before */
  addonBefore?: React.ReactNode
  /** Precision (decimal places) */
  precision?: number
  /** Controls (up/down arrows) */
  controls?: boolean
}

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      className,
      onChange,
      prefix,
      addonAfter,
      addonBefore,
      precision,
      controls = true,
      value,
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      if (raw === "" || raw === "-") {
        onChange?.(null)
        return
      }
      const num = parseFloat(raw)
      if (!isNaN(num)) {
        onChange?.(precision !== undefined ? parseFloat(num.toFixed(precision)) : num)
      }
    }

    const hasAddons = addonBefore || addonAfter

    const input = (
      <input
        ref={ref}
        type="number"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
          "ring-offset-background placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !controls && "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          hasAddons && "rounded-none first:rounded-l-md last:rounded-r-md",
          prefix && "pl-8",
          className
        )}
        value={value ?? ""}
        onChange={handleChange}
        {...props}
      />
    )

    if (hasAddons || prefix) {
      return (
        <div className="flex items-stretch">
          {addonBefore && (
            <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground">
              {addonBefore}
            </span>
          )}
          <div className="relative flex-1">
            {prefix && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                {prefix}
              </span>
            )}
            {input}
          </div>
          {addonAfter && (
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-input bg-muted px-3 text-sm text-muted-foreground">
              {addonAfter}
            </span>
          )}
        </div>
      )
    }

    return input
  }
)
InputNumber.displayName = "InputNumber"

export { Input, InputNumber }
